import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, User, Clock, Loader2, CheckCircle, Music, Save, Eye, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  pending: { label: 'Pendiente', color: 'text-yellow-400 bg-yellow-400/10' },
  in_progress: { label: 'En produccion', color: 'text-orange-400 bg-orange-400/10' },
  delivered: { label: 'Entregado', color: 'text-green-400 bg-green-400/10' },
};

const UltimateRequestsPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', songs_delivered: 0, staff_notes: '' });
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ultimate/staff/requests`, { withCredentials: true });
      setRequests(r.data);
    } catch {}
    setLoading(false);
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({ status: r.status, songs_delivered: r.songs_delivered, staff_notes: r.staff_notes || '' });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API_URL}/api/ultimate/staff/requests/${id}`, editForm, { withCredentials: true });
      await load();
      setEditingId(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="ultimate-requests-panel">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#E8720C]" /> Solicitudes Ultimate Pack
        </h2>
        <p className="text-xs text-white/40 mt-1">Clientes que compraron el plan y enviaron su estilo deseado</p>
      </div>

      {requests.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Music className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/40">Aun no hay solicitudes de Ultimate Pack.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const s = STATUS[r.status] || { label: r.status, color: 'text-white/50 bg-white/5' };
            const isEditing = editingId === r.id;
            return (
              <div key={r.id} className="glass-card p-4" data-testid={`ult-request-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{r.genre}</p>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.color}`}>{s.label}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E8720C]/15 text-[#E8720C]">
                        {r.songs_delivered} / {r.total_songs} canciones
                      </span>
                    </div>
                    <p className="text-xs text-white/40 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.user_name || r.user_email}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                  <button onClick={() => setViewing(r)}
                    className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg text-xs flex items-center gap-1.5"
                    data-testid={`view-request-${r.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Ver detalles
                  </button>
                </div>

                <p className="text-xs text-white/60 italic mb-3 p-2 bg-white/[0.02] rounded border-l-2 border-[#E8720C]/30">
                  {r.style_description}
                </p>

                {isEditing ? (
                  <div className="space-y-2 p-3 bg-white/[0.03] rounded-lg">
                    <div className="flex items-center gap-2 flex-wrap">
                      <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                        className="input-glass text-xs py-1.5" data-testid={`status-select-${r.id}`}>
                        <option value="pending">Pendiente</option>
                        <option value="in_progress">En produccion</option>
                        <option value="delivered">Entregado</option>
                      </select>
                      <input type="number" min="0" max="50" value={editForm.songs_delivered}
                        onChange={e => setEditForm({ ...editForm, songs_delivered: parseInt(e.target.value || '0') })}
                        className="input-glass text-xs py-1.5 w-24" placeholder="Entregadas"
                        data-testid={`songs-delivered-${r.id}`} />
                      <span className="text-xs text-white/40">/ {r.total_songs}</span>
                    </div>
                    <textarea value={editForm.staff_notes}
                      onChange={e => setEditForm({ ...editForm, staff_notes: e.target.value })}
                      placeholder="Nota para el cliente (opcional)"
                      rows={2} className="w-full input-glass text-xs"
                      data-testid={`staff-notes-${r.id}`} />
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(r.id)}
                        className="px-3 py-1.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold flex items-center gap-1.5"
                        data-testid={`save-request-${r.id}`}>
                        <Save className="w-3 h-3" /> Guardar
                      </button>
                      <button onClick={() => setEditingId(null)}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg text-xs">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => startEdit(r)}
                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                    data-testid={`edit-request-${r.id}`}>
                    {r.status === 'delivered' ? <CheckCircle className="w-3.5 h-3.5 text-green-400" /> : null}
                    Actualizar estado
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="glass-card p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2"><Crown className="w-4 h-4 text-[#E8720C]" /> {viewing.genre}</h3>
              <button onClick={() => setViewing(null)}><X className="w-4 h-4 text-white/50" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <Field label="Cliente" value={viewing.user_name} />
              <Field label="Email" value={viewing.user_email} />
              <Field label="Genero" value={viewing.genre} />
              <Field label="Descripcion del estilo" value={viewing.style_description} multiline />
              <Field label="Artistas de referencia" value={viewing.reference_artists} />
              <Field label="Canciones de referencia" value={viewing.reference_songs} />
              <Field label="Notas adicionales" value={viewing.additional_notes} multiline />
              <Field label="Creada" value={new Date(viewing.created_at).toLocaleString()} />
              {viewing.delivered_at && <Field label="Entregada" value={new Date(viewing.delivered_at).toLocaleString()} />}
              {viewing.staff_notes && <Field label="Nota del equipo" value={viewing.staff_notes} multiline highlighted />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, multiline, highlighted }) => {
  if (!value) return null;
  return (
    <div className={multiline ? '' : 'flex items-start justify-between gap-3'}>
      <span className="text-[11px] text-white/40 uppercase tracking-wider shrink-0">{label}</span>
      <span className={`text-xs ${multiline ? 'block mt-1' : 'text-right'} ${highlighted ? 'text-[#E8720C]' : 'text-white/80'}`}>
        {value}
      </span>
    </div>
  );
};

export default UltimateRequestsPanel;
